import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaPhone, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './styles.css';

// Importando imagens de fallback (usadas quando a API não tiver imagem)
import imgSafti from '../../assets/agencias/safti.webp';
import imgRemaxDragao from '../../assets/agencias/remaxdragao.webp';
import imgFocus from '../../assets/agencias/focus.webp';
import imgExp from '../../assets/agencias/exp.webp';
import imgCasaPortela from '../../assets/agencias/casadaportela.webp';
import paises from '../../paises.json';

const Agencies = () => {
  const [selectedType, setSelectedType] = useState('Profissionais');
  const [location, setLocation] = useState('');
  const [name, setName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Controla quais números foram revelados (por id da agência)
  const [revealedPhones, setRevealedPhones] = useState({});

  const itemsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [agenciesFromApi, setAgenciesFromApi] = useState([]);

  // Mapa de países (code -> nome)
  const countryNameByCode = (() => {
    const map = {};
    try {
      const continents = paises?.continents || {};
      Object.values(continents).forEach((arr) => {
        (arr || []).forEach((c) => {
          if (c?.code && c?.nome && !map[c.code]) map[c.code] = c.nome;
        });
      });
      // também varrer Europa/América Latina se estiverem no mesmo nível
      ['Europa', 'América Latina'].forEach((key) => {
        const arr = paises?.[key];
        (arr || []).forEach((c) => {
          if (c?.code && c?.nome && !map[c.code]) map[c.code] = c.nome;
        });
      });
    } catch (_) {}
    return map;
  })();

  const resolveCountryName = (codeOrName) => {
    if (!codeOrName) return '';
    const code = String(codeOrName).toUpperCase();
    return countryNameByCode[code] || codeOrName; // se já vier "Brasil" devolve como está
  };

  // Gera variações de telefone alterando os últimos 3 dígitos de forma determinística
  const mutatePhone = (phone, seed) => {
    const digits = String(phone).replace(/\D/g, '').padStart(9, '0').slice(-9);
    const base = parseInt(digits.slice(-3), 10);
    const varied = ((base + (seed * 7)) % 1000).toString().padStart(3, '0');
    return digits.slice(0, 6) + varied;
  };

  // Carrega agências reais da API e mapeia para o formato da UI
  useEffect(() => {
    const fetchAgencies = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${import.meta.env.VITE_URL_API}/imobiliarias`, {
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        const list = Array.isArray(body) ? body : (Array.isArray(body?.dados) ? body.dados : []);
        const mapped = list.map((a, idx) => {
          const totals = a?.totaisPorTipo || {};
          return {
            id: a.codigoIMOBILIARIA ?? idx + 1,
            name: a.nomeIMOBILIARIA || 'Agência',
            logo: a.imagemIMOBILIARIA && a.imagemIMOBILIARIA.startsWith('http') ? a.imagemIMOBILIARIA : imgExp,
            address: a.enderecoIMOBILIARIA || '',
            city: resolveCountryName(a.paisIMOBILIARIA) || a.cidadeIMOBILIARIA || '',
            postalCode: a.cepIMOBILIARIA || '',
            phone: a.telefoneIMOBILIARIA || '',
            properties: {
              sale: { count: Number(totals.compra ?? a.imovelIMOBILIARIA) || 0, minPrice: null, maxPrice: null },
              rent: { count: Number(totals.arrendar) || 0, minPrice: null, maxPrice: null },
              holiday: { count: Number(totals.ferias) || 0, minPrice: null, maxPrice: null }
            },
            date: a.anoIMOBILIARIA || ''
          };
        });
        setAgenciesFromApi(mapped);
      } catch (err) {
        setApiError(err?.message || 'Falha ao carregar imobiliárias');
        setAgenciesFromApi([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAgencies();
  }, []);

  // Filtragem das agências (sobre a lista vinda da API)
  const filteredAgencies = agenciesFromApi.filter(agency => {
    const matchesName = name.trim() === '' || 
      agency.name.toLowerCase().includes(name.toLowerCase());
    const matchesLocation = location.trim() === '' || 
      `${agency.city} ${agency.postalCode}`.toLowerCase().includes(location.toLowerCase());
    return matchesName && matchesLocation;
  });

  // Cálculo da paginação
  const totalPages = Math.ceil(filteredAgencies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAgencies = filteredAgencies.slice(startIndex, endIndex);

  // Gerar array de páginas para exibição
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;
    
    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage <= maxVisiblePages) {
        for (let i = 2; i <= maxVisiblePages + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage > totalPages - maxVisiblePages) {
        pages.push('...');
        for (let i = totalPages - maxVisiblePages; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Handlers de navegação
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="agencies-page">
      <div className="agencies-hero">
        <div className="hero-content">
          <h1>Agências Imobiliárias em Portugal</h1>
          <p>Encontre os melhores profissionais do mercado imobiliário</p>
        </div>
      </div>

      <div className="agencies-content">
        <div className="search-filters">
          <div className="filter-group">
            <label>Tipo de empresa</label>
            <select 
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="filter-select"
            >
              <option value="Profissionais">Profissionais</option>
              <option value="Particulares">Particulares</option>
              <option value="Todos">Todos</option>
            </select>
          </div>

          <div className="filter-group location">
            <label>Localização</label>
            <div className="input-with-icon">
              <FaMapMarkerAlt className="input-icon" />
              <input
                type="text"
                placeholder="ex: distrito, concelho, freguesia"
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="filter-group">
            <label>Nome</label>
            <div className="input-with-icon">
              <FaSearch className="input-icon" />
              <input
                type="text"
                placeholder="Pesquisar por nome"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button className="search-button">
            Pesquisar
          </button>
        </div>

        <div className="results-info">
          <p>profissionais encontrados: <strong>{filteredAgencies.length}</strong></p>
          {apiError && (
            <p style={{ color: '#b91c1c' }}>Erro ao carregar imobiliárias: {apiError}</p>
          )}
        </div>

        <div className="agencies-list">
          {currentAgencies.map(agency => (
            <Link 
              to={`/agencies/${agency.id}`} 
              key={agency.id} 
              className="agency-card"
            >
              <div className="agency-logo">
                <img src={agency.logo} alt={agency.name} />
              </div>
              
              <div className="agency-info">
                <h2>{agency.name}</h2>
                <p className="agency-address">{agency.city}</p>

                <div className="agency-stats">
                  <div className="stat-group">
                    <span className="stat-label">VENDER:</span>
                    <span className="stat-value">{agency.properties.sale.count}</span>
                    {agency.properties.sale.minPrice != null && agency.properties.sale.maxPrice != null && (
                      <span className="stat-range">
                        de: {agency.properties.sale.minPrice.toLocaleString()}€ 
                        até: {agency.properties.sale.maxPrice.toLocaleString()}€
                      </span>
                    )}
                  </div>

                  <div className="stat-group">
                    <span className="stat-label">ARRENDAR:</span>
                    <span className="stat-value">{agency.properties.rent.count}</span>
                    {agency.properties.rent.minPrice != null && agency.properties.rent.maxPrice != null && (
                      <span className="stat-range">
                        de: {agency.properties.rent.minPrice.toLocaleString()}€ 
                        até: {agency.properties.rent.maxPrice.toLocaleString()}€
                      </span>
                    )}
                  </div>

                  <div className="stat-group">
                    <span className="stat-label">FÉRIAS:</span>
                    <span className="stat-value">{agency.properties.holiday.count}</span>
                  </div>
                </div>
              </div>

              <div className="agency-contact">
                <span className="agency-date">{agency.date}</span>
                <div className="phone-number" aria-label="Telefone da agência">
                  <FaPhone className="phone-icon" />
                  <span>{revealedPhones[agency.id] ? agency.phone : '*** *** ***'}</span>
                </div>
                <button
                  className="show-number"
                  onClick={(e) => {
                    e.preventDefault();
                    setRevealedPhones(prev => ({ ...prev, [agency.id]: true }));
                  }}
                  disabled={!!revealedPhones[agency.id]}
                >
                  {revealedPhones[agency.id] ? 'Número exibido' : 'Mostrar número'}
                </button>
              </div>
            </Link>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              className="page-button prev" 
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              &lt;
            </button>
            
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="page-ellipsis">...</span>
              ) : (
                <button
                  key={page}
                  className={`page-button ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              )
            ))}

            <button 
              className="page-button next"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              &gt;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Agencies; 